// Sugalarm — transient "speaking" banner (cycle 222).
//
// A full-width strip pinned to the bottom of the active surface that appears
// ONLY while status is being read aloud. It hosts the relocated StatusOrb
// (cycle 214/215, moved off the Now hero this cycle), the spoken sentence
// bound to `tts.text` with a zone-tinted progress underline, and a Stop
// control. When the read-out ends the banner fades back out and the screen
// returns to a clean hero.
//
// Mounted at the frame level (KioskFrame / WebFrame / MobileFrame portrait)
// so it pops up on any screen while speaking — the behavior the deleted
// cycle-34 equalizer banner had. NOT mounted on the mobile-landscape split
// pane or the bedside clock face (no spare room — cycle-214 scope decision).
//
// Presentation only: it BINDS the public `useTTS` surface (`speaking`/`text`/
// `progress`/`stop`). The Stop button stops SPEECH only — it is never an
// alarm/acknowledgment control, and the banner never renders over an urgent
// alarm (safety-quiet). Tokens only (zone tint via `color-mix` on the zone
// fill); motion is transform/opacity only; NO `filter`/`backdrop-filter` on
// the banner (a filter would establish a clip region that slices the orb's
// glow into a rectangle — the bug this cycle exists to avoid).

import { useEffect, useState } from "react";

import type { FrameVariant } from "../../hooks/useFrameVariant";
import { usePrefersReducedMotion } from "../../hooks/usePrefersReducedMotion";
import type { TTSState } from "../../hooks/useTTS";
import { zoneColor } from "../../lib/glucose";
import type { Zone } from "../../lib/types";
import { StatusOrb, type StatusOrbState } from "./StatusOrb";
import styles from "./SpeakingBanner.module.css";

interface Props {
  /** The public TTS handle. Only `speaking`/`text`/`progress`/`stop` are
   *  used here — `speak` is started elsewhere (header Speak-Status button /
   *  the auto-announce timer). */
  tts: TTSState;
  /** Current glucose zone — colors the progress underline + focus ring
   *  (urgent zones map to red via `zoneColor`). Cycle 394: the PANEL fill is
   *  no longer zone-tinted; see `--voice-panel-fill`. */
  zone: Zone;
  /** Episode-level urgent (isLive + urgent_low/urgent_high). When true the
   *  banner does NOT render — nothing shimmers over an alarm. */
  urgentActive: boolean;
  /** Surface — drives orb size + banner min-height only; the state logic is
   *  identical across surfaces. */
  variant: FrameVariant;
  /** Bottom offset so the banner clears the surface's bottom chrome (the
   *  kiosk bottom nav, the mobile TabBar + safe-area). Web hugs the bottom. */
  bottom?: string | number;
  /** Left offset so the banner clears a side rail (the kiosk side-nav rail);
   *  0 elsewhere (the content region already starts after the web/landscape
   *  rails, which own their own column). */
  left?: string | number;
}

// Kept mounted briefly after `speaking` flips false so it can fade out
// instead of vanishing.
const GRACE_MS = 320;

export function SpeakingBanner({
  tts,
  zone,
  urgentActive,
  variant,
  bottom = 0,
  left = 0,
}: Props) {
  const { speaking, text, progress, stop } = tts;
  const reduced = usePrefersReducedMotion();
  const [mounted, setMounted] = useState(speaking);

  useEffect(() => {
    if (speaking) {
      setMounted(true);
      return undefined;
    }
    const id = window.setTimeout(() => setMounted(false), GRACE_MS);
    return () => clearTimeout(id);
  }, [speaking]);

  // Safety-quiet: never paint over an urgent alarm (belt-and-suspenders —
  // useAutoAnnounce already suppresses status read-outs during an urgent).
  if (urgentActive) return null;
  if (!mounted) return null;

  // Zone-driven tint: urgent zones already resolve to red via `zoneColor`,
  // so the progress underline tracks the zone with no separate urgent branch.
  const accent = zoneColor(zone);
  const orbSize = variant === "mobile" ? 48 : variant === "web" ? 52 : 60;
  // Reduced motion → static orb; otherwise speaking while up, idle during the
  // brief fade-out grace.
  const orbState: StatusOrbState = reduced
    ? "frozen"
    : speaking
      ? "speaking"
      : "idle";
  const pct = Math.max(0, Math.min(1, progress));

  return (
    <div
      className={styles.banner}
      data-shown={speaking ? "1" : undefined}
      data-variant={variant}
      style={{
        bottom,
        left,
        // Zone tint for the progress underline + focus ring only. Cycle 394
        // moved the PANEL fill to the theme-invariant `--voice-panel-fill`
        // (a light-blue tinted surface), so the banner no longer sets a
        // zone-tinted background of its own.
        ["--banner-accent" as string]: accent,
      }}
      role="status"
      aria-live="polite"
    >
      {/* The relocated plasma orb. Tap = stop speaking (its idle/speak-toggle
          role isn't reachable here — the banner is only up while speaking). */}
      <StatusOrb
        state={orbState}
        size={orbSize}
        onTap={stop}
        ariaLabel="Stop speaking"
      />

      <div className={styles.body}>
        <div className={styles.caption}>{text}</div>
        <div className={styles.progressTrack} aria-hidden="true">
          <div
            className={styles.progressFill}
            style={{ transform: `scaleX(${pct})` }}
          />
        </div>
      </div>

      <button
        type="button"
        className={styles.stopBtn}
        onClick={stop}
        aria-label="Stop speaking"
      >
        Stop
      </button>
    </div>
  );
}
